import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../Central/selectors";
import { useCentralStore } from "../Central";
import { useTemplatesStore } from "../Templates";
import {
	CatalogsState,
	ExtendedCatalog,
	ExtendedCategory,
	TariffWithPricesViewType,
} from "./types";
import { SupabaseWriteTableEnum } from "@/lib/supabase/supabaseTypes";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { useServiceCatalogStore } from "../ServiceCatalog";

const VISIBLE_PRICE_LISTS_KEY = "catalogs-visible-price-lists";

const initialState = {
	catalogs: [],
	categories: [],
	tariffs: [],
	tariffsLookup: {},
	filteredTariffs: [],
	selectedCatalogId: null,
	selectedCategoryId: null,
	visiblePriceListIds: new Set<string>(),
	isLoadingCatalogs: false,
	isLoadingCategories: false,
	isLoadingTariffs: false,
	showCatalogsList: true,
	showCategoriesList: true,
};

const buildTariffsLookup = (
	tariffs: TariffWithPricesViewType[],
	deactivatedCatalogIds: Set<string>
) => {
	return tariffs.reduce(
		(acc, tariff) => {
			if (tariff.is_deactivated === true) {
				return acc;
			}
			if (
				tariff.catalog_id &&
				deactivatedCatalogIds.has(tariff.catalog_id)
			) {
				return acc;
			}
			if (tariff.code_e && tariff.id) {
				if (!acc[tariff.code_e]) {
					acc[tariff.code_e] = {};
				}
				acc[tariff.code_e][tariff.id] = tariff;
			}
			return acc;
		},
		{} as Record<string, Record<string, TariffWithPricesViewType>>
	);
};

const getDeactivatedCatalogIds = (catalogs: ExtendedCatalog[]): Set<string> =>
	new Set(
		catalogs
			.filter((catalog) => catalog.is_deactivated)
			.map((catalog) => catalog.id)
			.filter((id): id is string => id !== null)
	);

export const useCatalogsStore = create<CatalogsState>()(
	devtools(
		(set, get) => ({
			...initialState,

			loadCatalogs: async () => {
				set({ isLoadingCatalogs: true });
				const organizationId =
					useCentralStore.getState().organization?.id;
				const activatedRegions =
					useCentralStore.getState().activatedRegions;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when loading catalogs"
					);
					set({ isLoadingCatalogs: false });
					return;
				}

				try {
					// Get activated region IDs
					const activatedRegionIds =
						activatedRegions?.map((r) => r.region) || [];

					// Build the query to filter catalogs
					let query = supabase.from("catalogs_view").select("*");

					// Filter: include custom catalogs for this org OR default catalogs matching activated regions
					if (activatedRegionIds.length > 0) {
						// If regions are activated, show custom catalogs and default catalogs matching those regions
						query = query.or(
							`organization_id.eq.${organizationId},and(organization_id.is.null,region.in.(${activatedRegionIds.join(
								","
							)}))`
						);
					} else {
						// If no regions activated, only show custom catalogs
						query = query.eq("organization_id", organizationId);
					}

					const { data: catalogsData, error } = await query
						.order("catalog_type", { ascending: false }) // custom first, then default
						.order("name", { ascending: true });

					if (error) {
						Logger.error(
							"Error loading catalogs:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Laden der Kataloge",
							type: "error",
						});
						return;
					}

					const catalogs: ExtendedCatalog[] = (
						catalogsData || []
					).map((catalog) => ({
						...catalog,
						isSelected: catalog.id === get().selectedCatalogId,
					}));

					set({
						catalogs,
						isLoadingCatalogs: false,
					});
				} catch (error) {
					Logger.error(
						"Unexpected error loading catalogs:",
						{},
						String(error)
					);
					set({ isLoadingCatalogs: false });
				}
			},
			loadCategories: async (catalogId: string) => {
				set({ isLoadingCategories: true });
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when loading categories"
					);
					set({ isLoadingCategories: false });
					return;
				}

				try {
					// Load default categories for this catalog
					const { data: defaultCategories, error: defaultError } =
						await supabase
							.from("catalog_categories_default")
							.select("*")
							.eq("catalogs_default_id", catalogId)
							.order("name", { ascending: true });

					if (defaultError && defaultError.code !== "PGRST116") {
						Logger.error(
							"Error loading default categories:",
							{},
							defaultError.message
						);
					}

					// Load custom categories for this catalog
					const { data: customCategories, error: customError } =
						await supabase
							.from("catalog_categories_custom")
							.select("*")
							.eq("organization_id", organizationId)
							.or(
								`catalogs_custom_id.eq.${catalogId},catalogs_default_id.eq.${catalogId}`
							)
							.order("name", { ascending: true });

					if (customError && customError.code !== "PGRST116") {
						Logger.error(
							"Error loading custom categories:",
							{},
							customError.message
						);
					}

					// Combine and format categories
					const categories: ExtendedCategory[] = [];

					// Add default categories
					if (defaultCategories) {
						defaultCategories.forEach((cat) => {
							categories.push({
								id: cat.id,
								name: cat.name,
								catalog_id: cat.catalogs_default_id,
								is_custom: false,
								is_deactivated: false,
							});
						});
					}

					// Add custom categories
					if (customCategories) {
						customCategories.forEach((cat) => {
							categories.push({
								id: cat.id,
								name: cat.name,
								catalog_id:
									cat.catalogs_custom_id ||
									cat.catalogs_default_id,
								is_custom: true,
								is_deactivated: cat.is_deactivated,
							});
						});
					}

					// Sort categories by name
					categories.sort((a, b) => a.name.localeCompare(b.name));

					set({
						categories,
						isLoadingCategories: false,
					});
				} catch (error) {
					Logger.error(
						"Unexpected error loading categories:",
						{},
						String(error)
					);
					set({ isLoadingCategories: false });
				}
			},
			loadTariffsForCurrentCatalog: async (
				catalogId: string,
				categoryId?: string
			) => {
				set({ isLoadingTariffs: true });
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when loading tariffs"
					);
					set({ isLoadingTariffs: false });
					return;
				}

				try {
					// Use RPC function to get tariffs with price items joined
					const { data: tariffsData, error } = await supabase.rpc(
						"get_tariffs_with_prices",
						{
							p_catalog_id: catalogId,
							p_category_id: categoryId || undefined,
							p_organization_id: organizationId,
						}
					);

					if (error) {
						Logger.error(
							"Error loading tariffs:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Laden der Leistungen",
							type: "error",
						});
						set({ isLoadingTariffs: false });
						return;
					}

					const newTariffsForCurrentCatalog = (tariffsData ||
						[]) as TariffWithPricesViewType[];

					// Update the global tariffs by replacing tariffs for this catalog
					const currentTariffs = get().tariffs;
					const currentTariffsWithoutCurrentCatalog =
						currentTariffs.filter(
							(t) => t.catalog_id !== catalogId
						);
					const updatedGlobalTariffs = [
						...currentTariffsWithoutCurrentCatalog,
						...newTariffsForCurrentCatalog,
					];

					// Rebuild the nested lookup: code_e -> id -> tariff
					const updatedTariffsLookup = buildTariffsLookup(
						updatedGlobalTariffs,
						getDeactivatedCatalogIds(get().catalogs)
					);

					set({
						tariffs: updatedGlobalTariffs,
						tariffsLookup: updatedTariffsLookup,
						filteredTariffs: newTariffsForCurrentCatalog, // Update the filtered view
						isLoadingTariffs: false,
					});
				} catch (error) {
					Logger.error(
						"Unexpected error loading tariffs:",
						{},
						String(error)
					);
					set({ isLoadingTariffs: false });
				}
			},
			loadAllTariffs: async () => {
				set({ isLoadingTariffs: true });
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when loading all tariffs"
					);
					set({ isLoadingTariffs: false });
					return;
				}

				try {
					const allTariffs: TariffWithPricesViewType[] = [];
					let offset = 0;
					const batchSize = 1000;
					let hasMore = true;
					const maxRetries = 3;

					// Fetch tariffs in batches to handle >1000 records and timeouts
					while (hasMore) {
						let retryCount = 0;
						let success = false;

						// Retry logic for each page
						while (retryCount < maxRetries && !success) {
							const { data, error } = await supabase.rpc(
								"get_tariffs_with_prices",
								{
									p_organization_id: organizationId,
									p_limit: batchSize,
									p_offset: offset,
								}
							);

							if (error) {
								retryCount++;
								Logger.error(
									`Error loading tariffs at offset ${offset} (attempt ${retryCount}/${maxRetries}): ${error.message}`
								);

								if (retryCount < maxRetries) {
									// Wait before retrying with exponential backoff
									await new Promise((resolve) =>
										setTimeout(
											resolve,
											Math.pow(2, retryCount) * 100
										)
									);
									continue;
								} else {
									// Max retries reached, show error and stop
									showNotification({
										message: `Fehler beim Laden der Leistungen nach ${maxRetries} Versuchen (Seite ${
											Math.floor(offset / batchSize) + 1
										})`,
										type: "error",
									});
									break; // Exit retry loop
								}
							} else {
								// Success
								success = true;
								if (data && data.length > 0) {
									allTariffs.push(
										...(data as TariffWithPricesViewType[])
									);
									offset += batchSize;
									// If we got less than batchSize records, we've reached the end
									hasMore = data.length === batchSize;
								} else {
									hasMore = false;
								}
							}
						}

						// If we couldn't fetch this page after all retries, stop pagination
						if (!success) {
							hasMore = false;
						}
					}

					// Create nested lookup: code_e -> id -> tariff
					const tariffsLookup = buildTariffsLookup(
						allTariffs,
						getDeactivatedCatalogIds(get().catalogs)
					);

					set({
						tariffs: allTariffs,
						tariffsLookup,
						isLoadingTariffs: false,
					});

					Logger.log(
						`Loaded ${allTariffs.length} tariffs in ${Math.ceil(
							allTariffs.length / batchSize
						)} batches`
					);
				} catch (error) {
					Logger.error(
						"Unexpected error loading all tariffs: " + String(error)
					);
					showNotification({
						message:
							"Ein unerwarteter Fehler ist beim Laden der Leistungen aufgetreten",
						type: "error",
					});
					set({ isLoadingTariffs: false });
				}
			},
			selectCatalog: (catalogId: string | null) => {
				const allTariffs = get().tariffs;

				// Handle "all-catalogs" special case
				if (catalogId === "all-catalogs") {
					set({
						selectedCatalogId: "all-catalogs",
						selectedCategoryId: null,
						categories: [],
						filteredTariffs: allTariffs, // Show all tariffs
					});

					// Update the isSelected flag on catalogs
					const catalogs = get().catalogs.map((cat) => ({
						...cat,
						isSelected: false, // No individual catalog is selected
					}));
					set({ catalogs });
					return;
				}

				// Filter tariffs for the selected catalog
				const filteredTariffs = catalogId
					? allTariffs.filter(
							(tariff) => tariff.catalog_id === catalogId
						)
					: [];

				set({
					selectedCatalogId: catalogId,
					selectedCategoryId: null,
					categories: [],
					filteredTariffs,
				});

				// Update the isSelected flag on catalogs
				const catalogs = get().catalogs.map((cat) => ({
					...cat,
					isSelected: cat.id === catalogId,
				}));
				set({ catalogs });

				// Load categories for the selected catalog
				if (catalogId && catalogId !== "all-catalogs") {
					get().loadCategories(catalogId);
				}
			},
			selectCategory: (categoryId: string | null) => {
				const { selectedCatalogId, tariffs } = get();

				// Filter tariffs by catalog and category
				let filteredTariffs = tariffs;

				if (selectedCatalogId) {
					filteredTariffs = filteredTariffs.filter(
						(tariff) => tariff.catalog_id === selectedCatalogId
					);
				}

				if (categoryId) {
					filteredTariffs = filteredTariffs.filter(
						(tariff) => tariff.category_id === categoryId
					);
				}

				set({
					selectedCategoryId: categoryId,
					filteredTariffs,
				});
			},
			addCustomCatalog: async (name: string) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when adding catalog"
					);
					return;
				}

				try {
					const { data, error } = await supabase
						.from("catalogs_custom")
						.insert({
							name,
							organization_id: organizationId,
							is_deactivated: false,
						})
						.select()
						.single();

					if (error) {
						Logger.error(
							"Error adding custom catalog:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Erstellen des Katalogs",
							type: "error",
						});
						return;
					}

					showNotification({
						message: "Katalog erfolgreich erstellt",
						type: "success",
					});

					// Reload catalogs
					await get().loadCatalogs();
				} catch (error) {
					Logger.error(
						"Unexpected error adding catalog:",
						{},
						String(error)
					);
				}
			},
			addCustomCategory: async (
				catalogId: string,
				name: string,
				isCustomCatalog: boolean
			) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when adding category"
					);
					return;
				}

				try {
					const categoryData = {
						name,
						organization_id: organizationId,
						is_deactivated: false,
						...(isCustomCatalog
							? { catalogs_custom_id: catalogId }
							: { catalogs_default_id: catalogId }),
					};

					const { data, error } = await supabase
						.from("catalog_categories_custom")
						.insert(categoryData)
						.select()
						.single();

					if (error) {
						Logger.error(
							"Error adding custom category:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Erstellen der Kategorie",
							type: "error",
						});
						return;
					}

					showNotification({
						message: "Kategorie erfolgreich erstellt",
						type: "success",
					});

					// Reload categories for the current catalog
					await get().loadCategories(catalogId);
				} catch (error) {
					Logger.error(
						"Unexpected error adding category:",
						{},
						String(error)
					);
				}
			},
			addCustomTariff: async (
				catalogId: string,
				categoryId: string | null,
				code: string,
				codeE: string,
				isCustomCatalog: boolean
			) => {
				try {
					// Determine if category is custom
					let isCategoryCustom: boolean | null = null;
					if (categoryId) {
						const category = get().categories.find(
							(c) => c.id === categoryId
						);
						isCategoryCustom = category?.is_custom || false;
					}

					const { data, error } = await supabase.rpc(
						"create_custom_tariff",
						{
							p_code: code,
							p_code_e: codeE,
							p_catalog_id: catalogId,
							p_category_id: categoryId || undefined,
							p_is_custom_catalog: isCustomCatalog,
							p_is_custom_category: isCategoryCustom || undefined,
						}
					);

					if (error) {
						Logger.error(
							"Error calling create_custom_tariff RPC:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Erstellen der Leistung",
							type: "error",
						});
						return;
					}

					const result = data[0];

					if (!result || !result.success) {
						// Handle specific error codes
						if (result?.error_code === "DUPLICATE_CODE") {
							showNotification({
								message:
									"Eine Leistung mit diesem Code existiert bereits in diesem Katalog. Bearbeite sie oder erstelle eine Variante.",
								type: "warning",
							});
						} else if (result?.error_code === "UNAUTHORIZED") {
							showNotification({
								message: "Keine Berechtigung für diese Aktion",
								type: "error",
							});
						} else if (result?.error_code === "INVALID_INPUT") {
							showNotification({
								message: "Ungültige Eingabedaten",
								type: "error",
							});
						} else if (
							result?.message ===
							'duplicate key value violates unique constraint "unqiue_tariffs_custom_code_e_organization_id_idx"'
						) {
							showNotification({
								message:
									"Eine Leistung mit diesem Eingabecode existiert bereits",
								type: "error",
							});
						}

						Logger.error(
							"Failed to create tariff:",
							{},
							result?.message || "Unknown error"
						);
						return;
					}

					// Success
					showNotification({
						message: "Leistung erfolgreich erstellt",
						type: "success",
					});

					// Reload tariffs for the current catalog and category
					await get().loadTariffsForCurrentCatalog(
						catalogId,
						categoryId || undefined
					);

					return result.tariff_id;
				} catch (error) {
					Logger.error(
						"Unexpected error adding tariff:",
						{},
						String(error)
					);
					showNotification({
						message: "Ein unerwarteter Fehler ist aufgetreten",
						type: "error",
					});
				}
			},
			createTariffVariant: async (
				tariffId: string,
				isDefaultTariff: boolean,
				codeE: string
			) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when creating tariff variant"
					);
					showNotification({
						message: "Keine Organisation gefunden",
						type: "error",
					});
					return { success: false };
				}

				try {
					const { data, error } = await supabase.rpc(
						"create_tariff_variant",
						{
							p_id_of_existing_tariff: tariffId,
							p_existing_tariff_is_default_tariff:
								isDefaultTariff,
							p_code_e_for_new_tariff: codeE,
						}
					);

					if (error) {
						Logger.error(
							"Error calling create_tariff_variant RPC:",
							{},
							error.message
						);
						showNotification({
							message: "Fehler beim Erstellen der Variante",
							type: "error",
						});
						return { success: false };
					}

					const result = data as {
						success: boolean;
						error_code: string;
						message: string;
					};

					if (!result || !result.success) {
						// Handle specific error codes
						if (result?.error_code === "DUPLICATE_CODE_E") {
							showNotification({
								message:
									"Eine Leistung mit diesem Eingabecode existiert bereits",
								type: "warning",
							});
						} else if (result?.error_code === "NO_CODE") {
							showNotification({
								message: "Die Originalleistung hat keinen Code",
								type: "warning",
							});
						} else if (result?.error_code === "TARIFF_NOT_FOUND") {
							showNotification({
								message: "Leistung nicht gefunden",
								type: "error",
							});
						} else {
							showNotification({
								message:
									result?.message ||
									"Fehler beim Erstellen der Variante",
								type: "error",
							});
						}

						Logger.error(
							"Failed to create tariff variant:",
							{},
							result?.message || "Unknown error"
						);
						return { success: false };
					}

					let affectedTemplateIds: number[] = [];

					if (isDefaultTariff) {
						const { checkTemplateUsage, reassignTemplateItems } =
							useTemplatesStore.getState();
						const templateIds = await checkTemplateUsage({
							tariffs_default_id: tariffId,
						});

						const { data: newTariff } = await supabase
							.from("tariffs_custom")
							.select("id")
							.eq("code_e", codeE)
							.eq("organization_id", organizationId)
							.maybeSingle();

						if (templateIds.length > 0 && newTariff?.id) {
							await reassignTemplateItems(
								{ tariffs_default_id: tariffId },
								{
									tariffs_default_id: null,
									tariffs_custom_id: newTariff.id,
								}
							);
							affectedTemplateIds = templateIds;
						}

						if (newTariff?.id) {
							const catalogStore =
								useServiceCatalogStore.getState();
							const usage = catalogStore.checkItemUsage({
								tariffs_default_id: tariffId,
							});
							if (usage.length > 0) {
								catalogStore.replaceItemGlobally(
									{ tariffs_default_id: tariffId },
									{ tariffs_custom_id: newTariff.id }
								);
								await catalogStore.saveCatalog();
							}
						}
					}

					showNotification({
						message: "Variante erfolgreich erstellt",
						type: "success",
					});

					await new Promise((resolve) => setTimeout(resolve, 100));

					const { selectedCatalogId, selectedCategoryId } = get();
					if (selectedCatalogId) {
						await get().loadTariffsForCurrentCatalog(
							selectedCatalogId,
							selectedCategoryId || undefined
						);
					}

					return {
						success: true as const,
						affectedTemplateIds:
							affectedTemplateIds.length > 0
								? affectedTemplateIds
								: undefined,
					};
				} catch (error) {
					Logger.error(
						"Unexpected error creating tariff variant:",
						{},
						String(error)
					);
					showNotification({
						message: "Ein unerwarteter Fehler ist aufgetreten",
						type: "error",
					});
					return { success: false };
				}
			},
			updateCustomTariff: async (
				tariffId: string,
				updates: Partial<{
					code: string;
					code_e: string;
					description_de: string;
					description_fr: string;
					description_it: string;
					standard_quantity: number;
					own_tp: number;
					is_req_dr_tax: boolean;
					additional_text: string;
					composition: string;
					is_deactivated: boolean;
				}>
			) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when updating tariff"
					);
					showNotification({
						message: "Keine Organisation gefunden",
						type: "error",
					});
					return { success: false };
				}

				const currentTariff = get().tariffs.find(
					(t) => t.id === tariffId
				);
				const oldCodeE = currentTariff?.code_e;
				const newCodeE = updates.code_e;
				const isCodeEChanging =
					newCodeE !== undefined && newCodeE !== oldCodeE;

				const originalTariffs = get().tariffs;
				const originalFilteredTariffs = get().filteredTariffs;
				const originalTariffsLookup = get().tariffsLookup;

				set((state) => {
					const newTariffs = state.tariffs.map(
						(tariff: TariffWithPricesViewType) =>
							tariff.id === tariffId
								? { ...tariff, ...updates }
								: tariff
					);
					const newFilteredTariffs = state.filteredTariffs.map(
						(tariff: TariffWithPricesViewType) =>
							tariff.id === tariffId
								? { ...tariff, ...updates }
								: tariff
					);
					const newTariffsLookup = buildTariffsLookup(
						newTariffs,
						getDeactivatedCatalogIds(state.catalogs)
					);
					return {
						tariffs: newTariffs,
						filteredTariffs: newFilteredTariffs,
						tariffsLookup: newTariffsLookup,
					};
				});

				try {
					const { error } = await supabase
						.from(SupabaseWriteTableEnum.TARIFFS_CUSTOM)
						.update(updates)
						.eq("id", tariffId)
						.eq("organization_id", organizationId);

					if (error) {
						set({
							tariffs: originalTariffs,
							filteredTariffs: originalFilteredTariffs,
							tariffsLookup: originalTariffsLookup,
						});

						Logger.error(
							"Error updating tariff:",
							{},
							error.message
						);

						if (
							error.message.includes(
								"duplicate key value violates unique constraint"
							)
						) {
							showNotification({
								message:
									"Tarif mit gleichem Eingabecode existiert bereits",
								type: "error",
							});
						} else {
							showNotification({
								message:
									"Fehler beim Aktualisieren der Leistung",
								type: "error",
							});
						}
						return { success: false };
					}

					let affectedTemplateIds: number[] = [];
					let reassignDirection: "to_default" | "to_custom" | null =
						null;
					let codeEChangeInfo:
						| {
								wasOverwritingDefault: boolean;
								oldDefaultId: string;
								newCodeE: string;
								oldCodeE: string;
								affectedTemplateIds: number[];
						  }
						| undefined;

					if (isCodeEChanging && currentTariff?.catalog_id) {
						const { checkTemplateUsage, reassignTemplateItems } =
							useTemplatesStore.getState();

						// Check if NEW code_e matches a default tariff (custom now overrides default)
						// Auto-assign templates from default to custom
						if (newCodeE) {
							const newDefaultTariff =
								await get().findMatchingDefaultTariff(
									newCodeE,
									currentTariff.catalog_id
								);
							if (newDefaultTariff) {
								const templateIds = await checkTemplateUsage({
									tariffs_default_id: newDefaultTariff.id,
								});
								if (templateIds.length > 0) {
									await reassignTemplateItems(
										{
											tariffs_default_id:
												newDefaultTariff.id,
										},
										{
											tariffs_default_id: null,
											tariffs_custom_id: tariffId,
										}
									);
									affectedTemplateIds = templateIds;
									reassignDirection = "to_custom";
								}
							}
						}

						// Check if OLD code_e matched a default tariff (custom no longer overrides)
						// Return info for dialog instead of auto-reassigning
						if (affectedTemplateIds.length === 0 && oldCodeE) {
							const oldDefaultTariff =
								await get().findMatchingDefaultTariff(
									oldCodeE,
									currentTariff.catalog_id
								);
							if (oldDefaultTariff) {
								const templateIds = await checkTemplateUsage({
									tariffs_custom_id: tariffId,
								});
								if (templateIds.length > 0) {
									codeEChangeInfo = {
										wasOverwritingDefault: true,
										oldDefaultId: oldDefaultTariff.id,
										newCodeE: newCodeE || "",
										oldCodeE: oldCodeE,
										affectedTemplateIds: templateIds,
									};
								}
							}
						}
					}

					showNotification({
						message: "Leistung erfolgreich aktualisiert",
						type: "success",
					});

					return {
						success: true,
						affectedTemplateIds:
							affectedTemplateIds.length > 0
								? affectedTemplateIds
								: undefined,
						reassignDirection: reassignDirection || undefined,
						codeEChangeInfo,
					};
				} catch (error) {
					set({
						tariffs: originalTariffs,
						tariffsLookup: originalTariffsLookup,
					});

					Logger.error(
						"Unexpected error updating tariff:",
						{},
						String(error)
					);
					showNotification({
						message: "Ein unerwarteter Fehler ist aufgetreten",
						type: "error",
					});
					return { success: false };
				}
			},

			setCatalogDeactivated: async (
				catalogId: string,
				isDeactivated: boolean
			) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when changing catalog activation"
					);
					return;
				}

				const catalog = get().catalogs.find((c) => c.id === catalogId);
				if (!catalog) {
					Logger.error(
						"Catalog not found when changing activation",
						{},
						catalogId
					);
					return;
				}

				try {
					// Custom catalogs carry their own is_deactivated column;
					// default catalogs are shared, so per-org deactivation is a
					// row in catalogs_default_deactivated.
					let errorMessage: string | null = null;
					if (catalog.catalog_type === "custom") {
						const { error } = await supabase
							.from("catalogs_custom")
							.update({ is_deactivated: isDeactivated })
							.eq("id", catalogId)
							.eq("organization_id", organizationId);
						errorMessage = error?.message ?? null;
					} else if (isDeactivated) {
						const { error } = await supabase
							.from("catalogs_default_deactivated")
							.insert({
								organization_id: organizationId,
								catalogs_default_id: catalogId,
							});
						errorMessage = error?.message ?? null;
					} else {
						const { error } = await supabase
							.from("catalogs_default_deactivated")
							.delete()
							.eq("organization_id", organizationId)
							.eq("catalogs_default_id", catalogId);
						errorMessage = error?.message ?? null;
					}

					if (errorMessage) {
						Logger.error(
							"Error changing catalog activation:",
							{},
							errorMessage
						);
						showNotification({
							message: isDeactivated
								? "Fehler beim Deaktivieren des Katalogs"
								: "Fehler beim Aktivieren des Katalogs",
							type: "error",
						});
						return;
					}

					showNotification({
						message: isDeactivated
							? "Katalog erfolgreich deaktiviert"
							: "Katalog erfolgreich aktiviert",
						type: "success",
					});

					// Refresh catalog activation state, then rebuild the matching
					// lookup so the code-collision selection dialog reflects the
					// change immediately, without a full tariff reload.
					await get().loadCatalogs();
					set({
						tariffsLookup: buildTariffsLookup(
							get().tariffs,
							getDeactivatedCatalogIds(get().catalogs)
						),
					});

					// If the now-deactivated catalog was selected, clear selection
					if (
						isDeactivated &&
						get().selectedCatalogId === catalogId
					) {
						get().selectCatalog(null);
					}
				} catch (error) {
					Logger.error(
						"Unexpected error changing catalog activation:",
						{},
						String(error)
					);
				}
			},
			updatePriceListItem: async (
				priceListItemId: number | null,
				priceListId: string,
				tariffId: string,
				isCustomTariff: boolean,
				price: number
			) => {
				const organizationId =
					useCentralStore.getState().organization?.id;

				if (!organizationId) {
					Logger.error(
						"No organization ID found when updating price list item"
					);
					return false;
				}

				try {
					let resultItemId = priceListItemId;

					if (priceListItemId) {
						// Update existing price list item
						const { error } = await supabase
							.from("price_list_items")
							.update({
								price,
								modified_at: new Date().toISOString(),
							})
							.eq("id", priceListItemId);

						if (error) {
							Logger.error(
								"Error updating price list item:",
								{},
								error.message
							);
							showNotification({
								message:
									"Fehler beim Aktualisieren des Preises",
								type: "error",
							});
							return false;
						}
					} else {
						// Create new price list item
						const newItem: any = {
							price,
							price_list_id: priceListId,
						};

						if (isCustomTariff) {
							newItem.tariffs_custom_id = tariffId;
						} else {
							newItem.tariffs_default_id = tariffId;
						}

						const { data, error } = await supabase
							.from("price_list_items")
							.insert(newItem)
							.select()
							.single();

						if (error) {
							Logger.error(
								"Error creating price list item:",
								{},
								error.message
							);
							showNotification({
								message: "Fehler beim Erstellen des Preises",
								type: "error",
							});
							return false;
						}

						resultItemId = data?.id;
					}

					// Get price list details for the price item
					const priceLists = useCentralStore.getState().priceLists;
					const priceList = priceLists.find(
						(pl) => pl.id === priceListId
					);

					// Update state in place instead of reloading all tariffs
					set((state) => {
						// Update main tariffs array
						const newTariffs = state.tariffs.map((tariff) => {
							if (tariff.id === tariffId) {
								const newPriceItems = [
									...(tariff.price_items || []),
								];
								const itemIndex = newPriceItems.findIndex(
									(item) =>
										item?.price_list_id === priceListId
								);

								const updatedPriceItem = {
									price_list_item_id: resultItemId,
									price,
									price_min: null,
									price_max: null,
									price_list_id: priceListId,
									price_list_name: priceList?.name || null,
									modified_by:
										useCentralStore.getState().userId, // should be set by a trigger on the database
								};

								if (itemIndex >= 0) {
									// Update existing price item
									newPriceItems[itemIndex] = updatedPriceItem;
								} else {
									// Add new price item
									newPriceItems.push(updatedPriceItem);
								}

								return {
									...tariff,
									price_items: newPriceItems,
								};
							}
							return tariff;
						});

						// Update filtered tariffs array
						const newFilteredTariffs = state.filteredTariffs.map(
							(tariff) => {
								if (tariff.id === tariffId) {
									const updatedTariff = newTariffs.find(
										(t) => t.id === tariffId
									);
									return updatedTariff || tariff;
								}
								return tariff;
							}
						);

						// Rebuild the nested tariffs lookup
						const newTariffsLookup = buildTariffsLookup(
							newTariffs,
							getDeactivatedCatalogIds(state.catalogs)
						);

						return {
							tariffs: newTariffs,
							filteredTariffs: newFilteredTariffs,
							tariffsLookup: newTariffsLookup,
						};
					});

					return true;
				} catch (error) {
					Logger.error(
						"Unexpected error updating price list item:",
						{},
						String(error)
					);
					showNotification({
						message: "Ein unerwarteter Fehler ist aufgetreten",
						type: "error",
					});
					return false;
				}
			},
			togglePriceListVisibility: (priceListId: string) => {
				const visiblePriceListIds = new Set(get().visiblePriceListIds);
				if (visiblePriceListIds.has(priceListId)) {
					visiblePriceListIds.delete(priceListId);
				} else {
					visiblePriceListIds.add(priceListId);
				}
				setLocalStorage(VISIBLE_PRICE_LISTS_KEY, [
					...visiblePriceListIds,
				]);
				set({ visiblePriceListIds });
			},
			setVisiblePriceLists: (priceListIds: string[]) => {
				const visiblePriceListIds = new Set(priceListIds);
				setLocalStorage(VISIBLE_PRICE_LISTS_KEY, [
					...visiblePriceListIds,
				]);
				set({ visiblePriceListIds });
			},

			initialize: async () => {
				// Load visible price lists from localStorage, or set defaults
				const savedPriceListIds = getLocalStorage<string[]>(
					VISIBLE_PRICE_LISTS_KEY,
					[]
				);
				if (savedPriceListIds.length > 0) {
					set({ visiblePriceListIds: new Set(savedPriceListIds) });
				} else {
					const priceLists = useCentralStore.getState().priceLists;
					if (priceLists.length > 0) {
						const defaultIds = priceLists
							.slice(0, 3)
							.map((pl) => pl.id);
						setLocalStorage(VISIBLE_PRICE_LISTS_KEY, defaultIds);
						set({ visiblePriceListIds: new Set(defaultIds) });
					}
				}

				// Load sequentially because the loadAllTariffs function needs the loaded catalogs
				await get().loadCatalogs();
				await get().loadAllTariffs();
			},

			reset: () => {
				set(initialState);
			},

			toggleCatalogsList: () => {
				set({ showCatalogsList: !get().showCatalogsList });
			},

			toggleCategoriesList: () => {
				set({ showCategoriesList: !get().showCategoriesList });
			},

			findMatchingDefaultTariff: async (
				codeE: string,
				catalogId: string
			) => {
				const { data, error } = await supabase
					.from("tariffs_default")
					.select("id, code")
					.eq("code", codeE)
					.eq("catalogs_default_id", catalogId)
					.maybeSingle();

				if (error || !data) {
					return null;
				}
				return data;
			},
		}),
		{
			name: "catalogs-store",
		}
	)
);
